import dayjs from 'dayjs';
import { LearnerProgress } from '.';
import { LEARNING_PLAN_UNIT_TYPE } from '../../LearningPlan/LearningPlanUnit';

export const convertToCSV = (data: Record<string, string>[]) => {
  if (!data.length) return '';

  const headers = Object.keys(data[0]);
  const csvHeaders = headers.join(',');

  const csvRows = data.map((row) =>
    headers
      .map((header) => {
        const value = row[header];
        if (
          typeof value === 'string' &&
          (value.includes(',') || value.includes('"') || value.includes('\n'))
        ) {
          return `"${value.replace(/"/g, '""')}"`;
        }

        return value || '';
      })
      .join(',')
  );

  return [csvHeaders, ...csvRows].join('\n');
};

export function processProgressDataForCSVDownload(
  learners: LearnerProgress[]
): Record<string, string>[] {
  const maxNumOfficeHours = Math.max(
    ...learners.map((learner) => learner.officeHours?.officeHours.length ?? 0)
  );
  return learners.map((learner) => ({
    'Learner Name': learner.name,
    'Learner Email': learner.email,
    Status: learner.status,
    'Modules / Projects': processModulesProjects(learner),
    'Mentor Name': learner.mentor?.name ?? '-',
    'Mentor Email': learner.mentor?.email ?? '-',
    'Mentor Notes Summary': processMentorNotesSummary(learner),
    ...Array.from({ length: maxNumOfficeHours }, (_, i) => ({
      [`Office Hour ${i + 1}`]: parseOfficeHour(
        learner.officeHours?.officeHours?.[i] ?? undefined
      ),
    })).reduce((acc, curr) => ({ ...acc, ...curr }), {}),
    'Last Active At': learner.lastActiveDate
      ? dayjs.utc(learner.lastActiveDate).local().format('YYYY-MM-DD HH:mm:ss')
      : '-',
    'Total Plan Progress %': learner.planProgress
      ? `${learner.planProgress.toFixed(2)}%`
      : '-',
    'Mentor Activity Stats': processMentorActivityStats(learner),
    'General Activity Stats': processGeneralActivityStats(learner),
    'Average Difficulty': processAverageDifficulty(learner),
    'Difficulty Breakdown': processDifficultyBreakdown(learner),
    'Onboarding Answers': processOnboardingAnswers(learner),
  }));
}

function parseOfficeHour(
  officeHour:
    | {
        didAttend: boolean | null;
        notes: {
          goals: string;
          challenges: string;
          insights: string;
        } | null;
        timestamp: Date;
      }
    | undefined
) {
  return `Attended: ${officeHour?.didAttend ? 'Yes' : 'No'}

Goals
${officeHour?.notes?.goals ?? '-'}

Challenges
${officeHour?.notes?.challenges ?? '-'}

Insights
${officeHour?.notes?.insights ?? '-'}
`;
}

function processMentorActivityStats(learner: LearnerProgress) {
  const numMentorSOSRequests =
    learner.progress?.mentor?.numMentorSOSQuestions ?? null;
  const numAIQuestions = learner.progress?.mentor?.numAIQuestions ?? null;
  const numAIHintInteractions =
    learner.progress?.mentor?.numAIHintInteractions ?? null;
  const numAIWhatDoesThisMean =
    learner.progress?.mentor?.numAIWhatDoesThisMean ?? null;

  const stats = [];

  if (numMentorSOSRequests !== null) {
    stats.push(`# Mentor SOS Requests: ${numMentorSOSRequests}`);
  }

  if (numAIQuestions !== null) {
    stats.push(`# AI questions: ${numAIQuestions}`);
  }

  if (numAIHintInteractions !== null) {
    stats.push(`# AI hint interactions: ${numAIHintInteractions}`);
  }

  if (numAIWhatDoesThisMean !== null) {
    stats.push(`# AI What does this mean?: ${numAIWhatDoesThisMean}`);
  }

  return stats.length > 0 ? stats.join('\n') : '-';
}

function processGeneralActivityStats(learner: LearnerProgress) {
  const numDaysActive = learner.progress?.general?.numDaysActive ?? null;
  const numExercisesCompleted =
    learner.progress?.general?.numExercisesCompleted ?? null;
  const totalExerciseTime =
    learner.progress?.general?.totalExerciseTime ?? null;

  const stats = [];

  if (numDaysActive !== null) {
    stats.push(`# Days Active: ${numDaysActive}`);
  }

  if (numExercisesCompleted !== null) {
    stats.push(`# Exercises Completed: ${numExercisesCompleted}`);
  }

  if (totalExerciseTime !== null) {
    stats.push(`Total Exercise Time: ${totalExerciseTime}`);
  }

  return stats.length > 0 ? stats.join('\n') : '-';
}

function processAverageDifficulty(learner: LearnerProgress) {
  const averageDifficulty = learner.progress?.difficulty?.average;
  return averageDifficulty ? averageDifficulty.toFixed(1) : '-';
}

function processDifficultyBreakdown(learner: LearnerProgress) {
  const difficultyItems = learner.progress?.difficulty?.items;

  if (!difficultyItems || difficultyItems.length === 0) {
    return '-';
  }

  const breakdown = difficultyItems.map(
    (item, index) => `${index + 1}. ${item.name}: ${item.difficultyRating}/5`
  );

  return breakdown.join('\n');
}

function processOnboardingAnswers(learner: LearnerProgress) {
  const onboardingAnswers = learner.onboardingAnswers;

  if (!onboardingAnswers) {
    return '-';
  }

  const sections = [];

  // Skills section
  sections.push('SKILLS - Self-assessment:');
  if (
    !onboardingAnswers.skillRatings ||
    Object.keys(onboardingAnswers.skillRatings).length === 0
  ) {
    sections.push('Unknown');
  } else {
    Object.entries(onboardingAnswers.skillRatings).forEach(
      ([skill, rating], index) => {
        sections.push(`${index + 1}. ${skill} - ${rating}/10`);
      }
    );
  }

  // Goals section
  sections.push('');
  sections.push('GOALS:');
  sections.push(`Workflows: ${onboardingAnswers.commonTasks || '-'}`);
  sections.push(`Other: ${onboardingAnswers.additionalComments || '-'}`);

  return sections.join('\n');
}

function processMentorNotesSummary(learner: LearnerProgress) {
  return `${learner.officeHours?.notesSummary ?? '-'}

Generated at: ${
    learner.officeHours?.notesLastUpdatedAt
      ? dayjs
          .utc(learner.officeHours.notesLastUpdatedAt)
          .local()
          .format('YYYY-MM-DD HH:mm:ss')
      : '-'
  }`;
}

function processModulesProjects(learner: LearnerProgress) {
  const topics = learner.topics;

  if (!topics || topics.length === 0) {
    return '-';
  }

  const topicSections = topics.map((topic) => {
    const sections = [`${topic.name}:`];

    if (topic.details && topic.details.length > 0) {
      topic.details.forEach((detail, index) => {
        let progressText = '';

        // Check for simple completed/total format
        if (detail.completed !== undefined && detail.total !== undefined) {
          progressText = `${detail.completed}/${detail.total} exercises`;
        } else {
          // Check for core/stretch format
          const coreParts = [];
          const stretchParts = [];

          if (
            detail.coreCompleted !== undefined &&
            detail.coreTotal !== undefined
          ) {
            const activityType =
              detail.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
                ? 'activities'
                : 'exercises';
            coreParts.push(
              `${detail.coreCompleted}/${detail.coreTotal} CORE ${activityType}`
            );
          }

          if (
            detail.stretchCompleted !== undefined &&
            detail.stretchTotal !== undefined &&
            detail.stretchTotal > 0
          ) {
            const lessonType =
              detail.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
                ? 'lessons'
                : 'exercises';
            stretchParts.push(
              `${detail.stretchCompleted}/${detail.stretchTotal} STRETCH ${lessonType}`
            );
          }

          progressText = [...coreParts, ...stretchParts].join(' + ');
        }

        sections.push(`  ${index + 1}. ${detail.name} - ${progressText}`);
      });
    }

    return sections.join('\n');
  });

  return topicSections.join('\n\n');
}
